import { useRouteQuery } from '@vueuse/router'
import type {
  SearchOptions,
  SearchResponse,
  RequestOptions,
} from '@nuxtjs/algolia'
import type { Ref, ComputedRef } from 'vue'
import type { UseAlgoliaIndexSearchReturnType } from '~/composables/algolia/useAlgoliaIndexSearch'
import type { NuxtError } from 'nuxt/app'
import type { AsyncDataExecuteOptions } from '#app/composables/asyncData'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import {
  buildAlgoliaAnalyticsTags,
  buildTransactionalSearchKey,
} from '~/utils/searchEngineHelpers'
import { applyAlgoliaSearchAnalyticsGuard } from '~/utils/algoliaSearchAnalytics'
import type { SearchEngine } from '~/utils/searchSurfaces'

export const useTransactionalPageSearch = (
  algoliaSearch: Ref<UseAlgoliaIndexSearchReturnType<SearchEngineEventItem>>,
  currentIndexName: ComputedRef<string> | Ref<string> | string = ref(
    getEventListIndexName()
  ),
  algoliaSearchOptions?:
    | ComputedRef<RequestOptions & SearchOptions>
    | Ref<RequestOptions & SearchOptions>,
  hitsPerPagination: Ref<number | undefined> = ref(undefined)
) => {
  const {
    public: { appEnv },
  } = useRuntimeConfig()
  const queryParam = useRouteQuery('query')

  const algoliaSearchOptionsValue = computed(() =>
    toValue(algoliaSearchOptions)
  )
  const currentIndexNameValue = computed(() => toValue(currentIndexName))

  const computedKey = computed(() =>
    buildTransactionalSearchKey({
      indexName: currentIndexNameValue.value,
      searchQuery: queryParam.value?.toString(),
      options: algoliaSearchOptionsValue.value,
    })
  )

  return useAsyncData<SearchResponse<SearchEngineEventItem>>(
    computedKey.value,
    () => {
      const searchQuery =
        (algoliaSearchOptionsValue.value?.query as string) ||
        queryParam.value?.toString() ||
        ''

      return algoliaSearch.value.search({
        query: '',

        requestOptions: applyAlgoliaSearchAnalyticsGuard(
          {
            attributesToHighlight: [],

            facets: ['*'],
            hitsPerPage: hitsPerPagination.value,
            ...(algoliaSearchOptionsValue.value || {}),
            query: searchQuery,
            clickAnalytics: true,
            analyticsTags: buildAlgoliaAnalyticsTags(
              appEnv,
              algoliaSearchOptionsValue.value?.analyticsTags
            ),
          },
          searchQuery
        ),
      })
    }
  )
}

export const useTransactionalPageSearchSideEffects = (
  currentIndexName: Ref<string> = ref(getEventListIndexName()),
  loading: ComputedRef<boolean>,
  algoliaSearch: Ref<UseAlgoliaIndexSearchReturnType<SearchEngineEventItem>>,
  performSearch: (opts?: AsyncDataExecuteOptions) => Promise<void>,
  searchError: Ref<NuxtError<unknown> | null, NuxtError<unknown> | null>,
  /**
   * KFT-4028: lets the caller swap in an engine-aware constructor
   * (useSearchIndex) instead of hardcoding useAlgoliaIndexSearch, so this
   * stays in sync with however the caller built the initial instance.
   */
  createSearchInstance: (
    indexName: string
  ) => UseAlgoliaIndexSearchReturnType<SearchEngineEventItem> = useAlgoliaIndexSearch,
  /**
   * KFT-4028: Algolia sorts via replica indices, so `currentIndexName`
   * changing is the only refetch signal it needs. Typesense has one
   * collection — sort is a query-time param read fresh on every search call
   * — so `currentIndexName` never changes on sort change and nothing
   * refetches. Pass a value here (e.g. the computed typesense sort_by) to
   * refetch — without recreating the search instance — whenever it changes.
   */
  extraRefetchTrigger?: Ref<unknown> | ComputedRef<unknown>,
  /**
   * KFT-4115: the shared engine can switch after hydration (A/B force), so the
   * search instance — which snapshots the engine — is rebuilt and refetched.
   */
  searchEngine?: Readonly<Ref<SearchEngine>>
) => {
  const loadingIndicator = useLoadingIndicator()
  watchEffect(() => {
    if (loading.value) {
      loadingIndicator.start()
    } else {
      loadingIndicator.finish()
    }
  })

  // Re-fetch when the index name, the engine (KFT-4115) or the extra trigger
  // changes. One multi-source watcher so an engine switch that also changes
  // the index name (Algolia replica → Typesense base collection) and the
  // Typesense sort trigger in the same tick refetches only once.
  watch(
    [
      () => currentIndexName.value,
      () => searchEngine?.value,
      () => extraRefetchTrigger?.value,
    ],
    ([indexName, engine], [oldIndexName, oldEngine]) => {
      if (indexName !== oldIndexName || engine !== oldEngine) {
        // not sure why that type error happens, but this seems to make indexes update
        algoliaSearch.value = createSearchInstance(indexName)
      }
      performSearch()
    },
    { immediate: false }
  )

  watchEffect(() => {
    if (searchError.value) {
      loadingIndicator.finish()
    }
  })
}
